import React from "react";
import {
  Access,
  accessOptions,
  ACLEntryPermission,
  Permission,
  permissionLabels,
} from "types";
import { getAccessTranslationKey } from "../../../../../../utils";
import { useTranslation } from "react-i18next";

interface ACLSelectProps {
  permissions: Permission[];
  value: ACLEntryPermission[];
  onChange: (entries: ACLEntryPermission[]) => void;
  disabled?: boolean;
  loading?: boolean;
}

const ACLSelect: React.FC<ACLSelectProps> = ({
  permissions,
  value,
  onChange,
  disabled,
  loading,
}) => {
  const { t } = useTranslation();
  // Map current value to a lookup for quick access
  const valueMap = React.useMemo(() => {
    const map: Record<string, ACLEntryPermission> = {};
    value.forEach((entry) => {
      map[entry.permission] = entry;
    });
    return map;
  }, [value]);

  const handleSelect = (permission: Permission, access: Access) => {
    const newEntries = permissions.map((perm) => {
      if (perm === permission) {
        return { permission: perm, access: access as Access };
      }
      return (
        valueMap[perm] || { permission: perm, access: "NOT_SET" as Access }
      );
    });
    onChange(newEntries);
  };

  return (
    <div className="flex flex-col gap-4">
      {permissions.map((perm) => (
        <div key={perm} className="flex items-center justify-between gap-4">
          <span className="text-base font-medium text-navy-700 dark:text-white">
            {permissionLabels[perm] || perm}
          </span>
          <select
            className="rounded-md border border-gray-200 bg-white px-3 py-1 text-base dark:border-navy-600 dark:bg-navy-800"
            value={valueMap[perm]?.access || "NOT_SET"}
            onChange={(e) => handleSelect(perm, e.target.value as Access)}
            disabled={disabled || loading}
          >
            {accessOptions.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {t(getAccessTranslationKey(opt.value))}
              </option>
            ))}
          </select>
        </div>
      ))}
    </div>
  );
};

export default ACLSelect;
